import { fetchGeoData, type Predictions, type RadarPredictions, slugify } from '@sg/garnish';

import { getEnvVars } from '@order/utils';

import { type LocationSearchMachineEvents } from '../../LocationSearch.types';

// ─────────────────────────────────────────────────────────────────────────────

export async function selectPrediction(params: Params) {
  const { predictions, event } = params;
  const { placeId, shouldUseRadar = false } = event;

  if (!placeId) return;

  if (shouldUseRadar) {
    return selectRadarPrediction(predictions as RadarPredictions, placeId);
  }

  return selectGooglePrediction(predictions as Predictions, placeId);
}

// ─── Google Implementation ───────────────────────────────────────────────────

async function selectGooglePrediction(
  predictions: Predictions,
  placeId: string,
): Promise<Prediction | undefined> {
  const selectedPrediction = predictions.find(({ place_id }) => place_id === placeId);

  if (!selectedPrediction) return;

  const { description } = selectedPrediction;
  const geoData = await safelyGetGeoData(placeId, GOOGLE_API_KEY as string);
  const result = geoData?.results[0];
  const { lat, lng } = result?.geometry?.location ?? {};
  const types = result?.types ?? [];
  const bounds = extractBounds(result?.geometry?.bounds as GeometryBounds | undefined);

  return { description, lat, lng, types, bounds };
}

// ─── Radar Implementation ────────────────────────────────────────────────────

function selectRadarPrediction(
  predictions: RadarPredictions,
  placeId: string,
): Prediction | undefined {
  const selectedPrediction = predictions.find((prediction) => {
    const radarId = `${slugify(prediction.formattedAddress)}`;

    return radarId === placeId;
  });

  if (!selectedPrediction) return;

  const { formattedAddress, latitude, longitude, layer } = selectedPrediction;

  const types: readonly string[] = layer ? layer.split(',') : [];

  return {
    description: formattedAddress,
    lat: latitude,
    lng: longitude,
    types,
  };
}

// ─── Helpers ─────────────────────────────────────────────────────────────────

function extractBounds(geometryBounds: GeometryBounds) {
  const soutwestBounds = geometryBounds?.southwest;
  const northeastBounds = geometryBounds?.northeast;

  const hasValidSouthwest =
    typeof soutwestBounds?.lat === 'number' && typeof soutwestBounds?.lng === 'number';
  const hasValidNortheast =
    typeof northeastBounds?.lat === 'number' && typeof northeastBounds?.lng === 'number';

  if (!hasValidSouthwest || !hasValidNortheast) {
    return;
  }

  return {
    southwest: { lat: soutwestBounds.lat, lng: soutwestBounds.lng },
    northeast: { lat: northeastBounds.lat, lng: northeastBounds.lng },
  };
}

async function safelyGetGeoData(placeId: string, googlePlacesApiKey: string) {
  try {
    return await fetchGeoData({ placeId, apiKey: googlePlacesApiKey });
  } catch {
    return null;
  }
}

const { GOOGLE_API_KEY } = getEnvVars();

// ─── Types ───────────────────────────────────────────────────────────────────

type Params = {
  predictions: Predictions | RadarPredictions;
  event: SelectPredictionEvent;
};

type SelectPredictionEvent = Extract<LocationSearchMachineEvents, { type: 'SELECT_PREDICTION' }>;

type GeometryBounds =
  | {
      southwest?: { lat: number; lng: number };
      northeast?: { lat: number; lng: number };
    }
  | undefined;

type Prediction = {
  description: string;
  lat: number;
  lng: number;
  types: readonly string[];
  bounds?: {
    southwest: { lat: number; lng: number };
    northeast: { lat: number; lng: number };
  };
};
